import { getApiUrl } from '../../../utils/environment';
import axios from 'axios';

// Rate limiting constants
const RATE_LIMIT = {
  MAX_REQUESTS_PER_PERIOD: 8, // Reduced from 30 to 8 to avoid hitting OpenPhone's limit of 10
  PERIOD_MS: 60 * 1000, // 1 minute in milliseconds
  REQUEST_INTERVAL: 200, // Increased from 100 to 200ms between requests
  RETRY_DELAY: 5000, // Initial retry delay reduced to 5 seconds
  MAX_RETRY_DELAY: 60 * 1000, // Maximum retry delay of 1 minute
  BACKOFF_FACTOR: 1.5 // Exponential backoff factor
};

const CONTACTS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Service for interacting with OpenPhone API
 */
class OpenPhoneService {
  constructor() {
    this.apiUrl = '/api/public/openphone';
    this.contactsCache = new Map(); // Initialize as a Map instead of null
    this.contactsCacheTimestamp = null;
    this.phoneNumbersCache = null;
    this.apiConfigured = null;
    this.queue = [];
    this.processingQueue = false;
    this.requestCount = 0;
    this.periodStart = Date.now();
    this.pendingRequests = {}; // Initialize pendingRequests
    
    // Load cache from localStorage
    this.loadCacheFromStorage();
    
    // Set up periodic cache saving
    this.cacheInterval = setInterval(() => this.saveCacheToStorage(), 30000);
  }
  
  // Load cache from localStorage
  loadCacheFromStorage() {
    try {
      const cachedContacts = localStorage.getItem('openphone_contacts_cache');
      if (cachedContacts) {
        // Parse the cached contacts and convert to Map
        const parsed = JSON.parse(cachedContacts);
        // If it's an array of entries, convert it to a Map
        if (Array.isArray(parsed)) {
          this.contactsCache = new Map(parsed);
        } else if (typeof parsed === 'object') {
          // Handle if stored as an object instead of Map entries
          this.contactsCache = new Map(Object.entries(parsed));
        }
      }
      
      const cachedPhoneNumbers = localStorage.getItem('openphone_numbers_cache');
      if (cachedPhoneNumbers) {
        this.phoneNumbersCache = JSON.parse(cachedPhoneNumbers);
      }
    } catch (error) {
      console.error('Error loading OpenPhone cache from storage:', error);
      // Ensure we have valid defaults if loading fails
      this.contactsCache = new Map();
      this.phoneNumbersCache = [];
    }
  }
  
  // Save cache to localStorage
  saveCacheToStorage() {
    try {
      // Convert Map to array of entries for serialization
      if (this.contactsCache instanceof Map) {
        localStorage.setItem('openphone_contacts_cache', JSON.stringify(Array.from(this.contactsCache.entries())));
      }
      
      if (this.phoneNumbersCache) {
        localStorage.setItem('openphone_numbers_cache', JSON.stringify(this.phoneNumbersCache));
      }
    } catch (error) {
      console.error('Error saving OpenPhone cache to storage:', error);
    }
  }
  
  /**
   * Queue an API request to respect rate limits
   * @param {Function} requestFn - Function that returns a promise for the API request
   * @returns {Promise} - Promise that resolves with the API response
   */
  async queueRequest(requestFn) {
    return new Promise((resolve, reject) => {
      // Add request to the queue
      this.queue.push({ requestFn, resolve, reject });
      
      // Process the queue if not already processing
      if (!this.processingQueue) {
        this.processQueue();
      }
    });
  }
  
  /**
   * Process the request queue with rate limiting
   */
  async processQueue() {
    if (this.queue.length === 0 || this.processingQueue) {
      return;
    }

    this.processingQueue = true;

    try {
      while (this.queue.length > 0) {
        const now = Date.now();
        const elapsed = now - this.periodStart;

        // Check if we need to reset our rate limit counter
        if (elapsed > RATE_LIMIT.PERIOD_MS) {
          this.requestCount = 0;
          this.periodStart = now;
        }

        // Check if we've hit the rate limit
        if (this.requestCount >= RATE_LIMIT.MAX_REQUESTS_PER_PERIOD) {
          const waitTime = RATE_LIMIT.PERIOD_MS - elapsed + 100; // Add a small buffer
          console.log(`Rate limit reached. Waiting ${waitTime}ms before resuming.`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          this.requestCount = 0;
          this.periodStart = Date.now();
          continue;
        }

        // Process the next request
        const { requestFn, resolve, reject, retryCount = 0 } = this.queue.shift();
        
        try {
          this.requestCount++;
          const response = await requestFn();
          resolve(response);
        } catch (error) {
          // Check if we hit a rate limit error
          if (error.message.includes('Rate limit') || (error.response && error.response.status === 429)) {
            console.log(`Rate limit response received. Retry count: ${retryCount}`);
            
            // Calculate retry delay with exponential backoff
            const retryDelay = Math.min(
              RATE_LIMIT.RETRY_DELAY * Math.pow(RATE_LIMIT.BACKOFF_FACTOR, retryCount),
              RATE_LIMIT.MAX_RETRY_DELAY
            );
            
            console.log(`Backing off for ${retryDelay}ms before retrying.`);
            
            // Put the request back at the front of the queue with increased retry count
            this.queue.unshift({ 
              requestFn, 
              resolve, 
              reject,
              retryCount: retryCount + 1 
            });
            
            // Wait for the retry delay
            await new Promise(resolveDelay => setTimeout(resolveDelay, retryDelay));
            
            // Reset our counter
            this.requestCount = 0;
            this.periodStart = Date.now();
          } else {
            // For any other error, just reject the promise
            reject(error);
          }
        }

        // Add a small delay between processing items
        if (this.queue.length > 0) {
          await new Promise(resolve => setTimeout(resolve, RATE_LIMIT.REQUEST_INTERVAL));
        }
      }
    } catch (error) {
      console.error('Error in queue processing:', error);
    } finally {
      this.processingQueue = false;
      
      // If items were added while processing, restart processing
      if (this.queue.length > 0) {
        this.processQueue();
      }
    }
  }
  
  // Normalize phone number for consistent lookup
  normalizePhoneNumber(phoneNumber) {
    if (!phoneNumber) return null;
    
    // Remove all non-numeric characters
    let normalized = phoneNumber.replace(/\D/g, '');
    
    // If the number starts with a country code (e.g., 1 for US), ensure it's formatted correctly
    if (normalized.length === 10) {
      // Add US country code for 10-digit numbers
      normalized = '1' + normalized;
    }
    
    return normalized;
  }
  
  // Clean up resources
  destroy() {
    // Save cache one last time
    this.saveCacheToStorage();
    
    // Clear interval
    if (this.cacheInterval) {
      clearInterval(this.cacheInterval);
      this.cacheInterval = null;
    }
  }

  /**
   * Apply rate limiting to API requests
   * @returns {Promise<boolean>} - True if request should proceed, false if it should wait
   */
  async applyRateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < RATE_LIMIT.REQUEST_INTERVAL) {
      // Need to wait
      const delay = RATE_LIMIT.REQUEST_INTERVAL - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    this.lastRequestTime = Date.now();
    return true;
  }

  /**
   * Handle a rate limit response
   * @param {string} operation - Description of the operation that was rate limited
   */
  async handleRateLimit(operation) {
    console.warn(`Rate limited during ${operation}, will retry after delay`);
    await new Promise(resolve => setTimeout(resolve, RATE_LIMIT.RETRY_DELAY));
  }

  /**
   * Deduplicate API requests to prevent duplicate in-flight requests
   * @param {string} key - Unique key for the request
   * @param {Function} requestFn - Function that makes the actual request
   * @returns {Promise} - The result of the request
   */
  async deduplicateRequest(key, requestFn) {
    // If a request with this key is already in flight, return its promise
    if (this.pendingRequests[key]) {
      return this.pendingRequests[key];
    }
    
    // Create a new promise for this request
    const promise = (async () => {
      try {
        await this.applyRateLimit();
        return await requestFn();
      } finally {
        // Remove from pending requests when done
        delete this.pendingRequests[key];
      }
    })();
    
    // Store the promise
    this.pendingRequests[key] = promise;
    
    return promise;
  }

  /**
   * Send a text message to a phone number through OpenPhone
   * @param {string} phoneNumber - The phone number to text
   * @param {string} message - The message content
   * @returns {Promise<Object>} - The response from the OpenPhone API
   */
  async sendTextMessage(phoneNumber, message) {
    if (!phoneNumber || !message) {
      throw new Error('Phone number and message are required');
    }
    
    if (!this.isConfigured()) {
      throw new Error('OpenPhone API is not configured');
    }
    
    const formattedNumber = this.formatE164(phoneNumber);
    
    return this.queueRequest(async () => {
      try {
        const response = await axios.post(`${this.apiUrl}/messages`, {
          phoneNumber: formattedNumber,
          message
        });
        
        return response.data;
      } catch (error) {
        console.error('Error sending text message via OpenPhone:', error);
        throw error;
      }
    });
  }

  /**
   * Get contacts by phone number
   * This correctly implements the OpenPhone API which requires externalIds
   * @param {string} phoneNumber - The phone number to find contacts for
   * @returns {Promise} - The API response with matching contacts
   */
  async getContactByPhone(phoneNumber) {
    const formattedNumber = this.formatE164(phoneNumber);
    const key = `getContactByPhone:${formattedNumber}`;
    
    // Check cache first
    if (this.contactsCache.has(formattedNumber)) {
      const contactId = this.contactsCache.get(formattedNumber);
      // Return fake response object with the data
      return [{ id: contactId }];
    }
    
    return this.deduplicateRequest(key, async () => {
      try {
        // Search for contacts using the phone parameter
        // Using the phone number as the externalId (required parameter)
        const response = await fetch(`${this.apiUrl}?endpoint=contacts&externalIds=${encodeURIComponent(formattedNumber)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (response.status === 429) {
          await this.handleRateLimit('getting contact by phone');
          return [];
        }

        const data = await response.json();
        
        // If we got a valid response, update our contacts cache
        if (data && Array.isArray(data.data)) {
          data.data.forEach(contact => {
            if (contact.id) {
              // Store this contact ID for the queried phone number
              this.contactsCache.set(formattedNumber, contact.id);
              
              // Also store all other phone numbers associated with this contact
              if (contact.defaultFields && contact.defaultFields.phoneNumbers) {
                contact.defaultFields.phoneNumbers.forEach(phone => {
                  if (phone.value) {
                    this.contactsCache.set(this.formatE164(phone.value), contact.id);
                  }
                });
              }
            }
          });
          
          return data.data;
        }
        
        return [];
      } catch (error) {
        console.error('Error fetching OpenPhone contacts by phone:', error);
        return [];
      }
    });
  }
  
  /**
   * Initialize the service with some contact data
   * Only called once per session
   */
  async initialize() {
    if (this.initializationComplete) return;
    
    try {
      // Don't need to do anything if we already have cache data from localStorage
      if (this.contactsCache.size > 0) {
        this.initializationComplete = true;
        return;
      }
      
      // Otherwise do a single query to get some contact data
      await this.listContacts();
      this.initializationComplete = true;
    } catch (error) {
      console.error('Error initializing OpenPhone service:', error);
    }
  }
  
  /**
   * List contacts from OpenPhone
   * @returns {Promise<Array<Object>>} List of contacts
   */
  async listContacts() {
    if (!this.isConfigured()) {
      throw new Error('OpenPhone API is not configured');
    }
    
    // If we have a contacts cache and it's not too old, return it
    if (this.contactsCache && 
        this.contactsCacheTimestamp && 
        (Date.now() - this.contactsCacheTimestamp < RATE_LIMIT.CACHE_TTL)) {
      return this.contactsCache;
    }
    
    return this.queueRequest(async () => {
      try {
        const response = await axios.get(`${this.apiUrl}/contacts`);
        
        if (response.data && Array.isArray(response.data)) {
          // Update the cache with the new contacts
          this.contactsCache = response.data;
          this.contactsCacheTimestamp = Date.now();
          
          return response.data;
        }
        
        return [];
      } catch (error) {
        console.error('Error fetching OpenPhone contacts:', error);
        throw error;
      }
    });
  }

  /**
   * Find a contact by phone number
   * @param {string} phoneNumber - The phone number to search for
   * @returns {Promise<string|null>} - The contact ID or null if not found
   */
  async findContactByPhone(phoneNumber) {
    const formattedNumber = this.formatE164(phoneNumber);
    
    // Check cache first
    if (this.contactsCache.has(formattedNumber)) {
      return this.contactsCache.get(formattedNumber);
    }
    
    try {
      // Try to find the contact directly by phone number
      const contacts = await this.getContactByPhone(formattedNumber);
      
      // Check if we found any contacts
      if (contacts && contacts.length > 0 && contacts[0].id) {
        return contacts[0].id;
      }
      
      // If nothing found, return null
      return null;
    } catch (error) {
      console.error('Error finding contact by phone:', error);
      return null;
    }
  }

  /**
   * Verify a contact exists in OpenPhone by ID
   * @param {string} contactId - The OpenPhone contact ID to verify
   * @returns {Promise<boolean>} - True if contact exists
   */
  async verifyContactExists(contactId) {
    if (!contactId) return false;
    
    try {
      const response = await fetch(`${this.apiUrl}?endpoint=contacts/${contactId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // If we get a 200 response, the contact exists
      if (response.ok) {
        const data = await response.json();
        console.log('Verified contact exists in OpenPhone:', data);
        return true;
      }
      
      console.log(`Contact verification failed with status ${response.status}`);
      return false;
    } catch (error) {
      console.error('Error verifying contact:', error);
      return false;
    }
  }

  /**
   * Create a new contact in OpenPhone
   * @param {Object} contactData - The contact data
   * @param {string} contactData.firstName - First name
   * @param {string} contactData.lastName - Last name (optional)
   * @param {string} contactData.phoneNumber - Phone number
   * @param {string} contactData.email - Email (optional)
   * @param {string} contactData.company - Company (optional)
   * @returns {Promise<Object>} Created contact
   */
  async createContact(contactData) {
    if (!this.isConfigured()) {
      throw new Error('OpenPhone API is not configured');
    }
    
    if (!contactData || !contactData.phoneNumber) {
      throw new Error('Phone number is required to create a contact');
    }
    
    // Format the phone number to E.164
    const formattedPhone = this.formatE164(contactData.phoneNumber);
    if (!formattedPhone) {
      throw new Error(`Invalid phone number: ${contactData.phoneNumber}`);
    }
    
    return this.queueRequest(async () => {
      try {
        // First, check if contact already exists with this phone number
        const existingContacts = await this.getContactByPhone(formattedPhone);
        if (existingContacts && existingContacts.length > 0) {
          console.log('Contact already exists in OpenPhone:', existingContacts[0]);
          return { ...existingContacts[0], existingContact: true };
        }
        
        // Prepare contact data for API according to OpenPhone documentation
        const payload = {
          defaultFields: {
            firstName: contactData.firstName || '',
            lastName: contactData.lastName || '',
            phoneNumbers: [
              {
                name: 'mobile',
                value: formattedPhone
              }
            ]
          },
          source: 'public-api',
          externalId: formattedPhone // Use phone number as external ID for lookup
        };
        
        // Add optional fields if they exist
        if (contactData.email) {
          payload.defaultFields.emails = [
            {
              name: 'work',
              value: contactData.email
            }
          ];
        }
        
        if (contactData.company) {
          payload.defaultFields.company = contactData.company;
        }
        
        // Sleep for a short time to avoid rapid-fire requests
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const response = await fetch(`${this.apiUrl}?endpoint=contacts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        // Check for rate limiting
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        
        // Check for other errors
        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = `API error (${response.status})`;
          
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = `API error: ${JSON.stringify(errorData)}`;
          } catch (e) {
            // If we can't parse the error as JSON, use the text
            errorMessage = `API error: ${errorText}`;
          }
          
          throw new Error(errorMessage);
        }
        
        const data = await response.json();
        
        // If successful, update the contacts cache
        if (data && data.id) {
          const normalizedNumber = this.normalizePhoneNumber(formattedPhone);
          this.contactsCache.set(normalizedNumber, data.id);
          this.saveCacheToStorage();
          
          // Verify the contact exists
          await this.verifyContactExists(data.id);
          
          return data;
        } else if (data && data.data && data.data.id) {
          // Handle nested data response format
          const contactData = data.data;
          const normalizedNumber = this.normalizePhoneNumber(formattedPhone);
          this.contactsCache.set(normalizedNumber, contactData.id);
          this.saveCacheToStorage();
          
          // Verify the contact exists
          await this.verifyContactExists(contactData.id);
          
          console.log('Created contact in OpenPhone:', contactData);
          return contactData;
        } else {
          console.warn('Unexpected OpenPhone API response format:', data);
          return data;
        }
      } catch (error) {
        console.error('Error creating contact in OpenPhone:', error);
        throw error;
      }
    });
  }

  /**
   * Get the user's OpenPhone phone numbers
   * @returns {Promise<Array<string>>} List of phone numbers
   */
  async getPhoneNumbers() {
    if (!this.isConfigured()) {
      throw new Error('OpenPhone API is not configured');
    }
    
    // Return cached phone numbers if available
    if (this.phoneNumbersCache && this.phoneNumbersCache.length > 0) {
      return this.phoneNumbersCache;
    }
    
    return this.queueRequest(async () => {
      try {
        const response = await axios.get(`${this.apiUrl}/phone-numbers`);
        
        if (response.data && Array.isArray(response.data)) {
          // Extract the E.164 formatted numbers from the response
          const numbers = response.data.map(num => num.e164);
          
          // Cache the phone numbers
          this.phoneNumbersCache = numbers;
          
          return numbers;
        }
        
        return [];
      } catch (error) {
        console.error('Error fetching OpenPhone numbers:', error);
        throw error;
      }
    });
  }

  /**
   * Format phone number to E.164 format (+1XXXXXXXXXX)
   * @param {string} phoneNumber - The phone number to format
   * @returns {string} - Formatted phone number
   */
  formatE164(phoneNumber) {
    if (!phoneNumber) return '';
    
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Add country code if missing
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    } else if (cleaned.length > 10 && !cleaned.startsWith('+')) {
      return `+${cleaned}`;
    } else if (!cleaned.startsWith('+')) {
      return `+${cleaned}`;
    }
    
    return phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
  }

  /**
   * Generate an OpenPhone deep link for calling
   * @param {string} phoneNumber - The phone number to call
   * @returns {Promise<string>} - The OpenPhone deep link
   */
  async generateCallLink(phoneNumber) {
    if (!this.isConfigured()) {
      return null;
    }
    
    try {
      // Format the phone number
      const formattedNumber = this.formatE164(phoneNumber);
      const normalizedPhone = this.normalizePhoneNumber(phoneNumber);
      const encodedPhoneNumber = encodeURIComponent(formattedNumber.replace('+', ''));
      
      // Check if running on mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // If on mobile, use deep linking
      if (isMobile) {
        // Deep link format: openphone://dial?number=<number>&action=call
        return `openphone://dial?number=${encodedPhoneNumber}&action=call`;
      }
      
      // Try to find the contact in our cache first
      if (normalizedPhone && this.contactsCache[normalizedPhone]) {
        const contactId = this.contactsCache[normalizedPhone].id;
        return `https://my.openphone.com/contact/${contactId}`;
      }
      
      // If we can't find in cache, check if this contact exists in OpenPhone
      const contacts = await this.listContacts();
      
      // Check if contact already exists by phone number
      const existingContact = contacts.find(contact => 
        contact.phoneNumbers && contact.phoneNumbers.some(p => 
          this.normalizePhoneNumber(p.phoneNumber) === normalizedPhone
        )
      );
      
      if (existingContact) {
        // Cache the contact
        this.contactsCache[normalizedPhone] = existingContact;
        return `https://my.openphone.com/contact/${existingContact.id}`;
      }
      
      // If contact doesn't exist, return a generic call link
      return `https://my.openphone.com/call?to=${encodeURIComponent(formattedNumber)}`;
    } catch (error) {
      console.error('Error generating call link:', error);
      return 'https://my.openphone.com/inbox';
    }
  }

  /**
   * Generate an OpenPhone deep link for texting
   * @param {string} phoneNumber - The phone number to text
   * @param {string} message - Optional message to pre-fill
   * @returns {Promise<string>} - The OpenPhone deep link
   */
  async generateTextLink(phoneNumber, message = '') {
    if (!this.isConfigured()) {
      return null;
    }
    
    try {
      // Format the phone number
      const formattedNumber = this.formatE164(phoneNumber);
      const normalizedPhone = this.normalizePhoneNumber(phoneNumber);
      const encodedPhoneNumber = encodeURIComponent(formattedNumber.replace('+', ''));
      
      // Check if running on mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // If on mobile, use deep linking
      if (isMobile) {
        // Deep link format: openphone://message?number=<number>&text=<text>
        let deepLink = `openphone://message?number=${encodedPhoneNumber}`;
        if (message) {
          deepLink += `&text=${encodeURIComponent(message)}`;
        }
        return deepLink;
      }
      
      // Try to find the contact in our cache first
      if (normalizedPhone && this.contactsCache[normalizedPhone]) {
        const contactId = this.contactsCache[normalizedPhone].id;
        return `https://my.openphone.com/contact/${contactId}`;
      }
      
      // If not in cache, generate regular text link
      let textLink = `https://my.openphone.com/text?to=${encodeURIComponent(formattedNumber)}`;
      
      if (message) {
        textLink += `&message=${encodeURIComponent(message)}`;
      }
      
      return textLink;
    } catch (error) {
      console.error('Error generating text link:', error);
      return 'https://my.openphone.com/inbox';
    }
  }

  /**
   * Generate an OpenPhone contact URL for a specific contact
   * @param {string} contactId - The OpenPhone contact ID
   * @returns {string} - The OpenPhone contact URL
   */
  generateContactUrl(contactId) {
    return `https://my.openphone.com/contacts/${contactId}`;
  }

  /**
   * Check if the OpenPhone API is configured
   * @returns {Promise<boolean>} - True if configured, false otherwise
   */
  async isConfigured() {
    // Return cached value if available
    if (this.apiConfigured !== null) {
      return this.apiConfigured;
    }
    
    try {
      // Make a simple HEAD request to check if API is available
      const response = await fetch(`${this.apiUrl}`, {
        method: 'HEAD',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // If we get any 2xx or 4xx response, consider the API configured
      // Only 5xx responses indicate the API is not properly set up
      this.apiConfigured = response.status < 500;
      
      return this.apiConfigured;
    } catch (error) {
      console.error('Error checking OpenPhone configuration:', error);
      this.apiConfigured = false;
      return false;
    }
  }
}

// Export a singleton instance
const openPhoneServiceInstance = new OpenPhoneService();
export default openPhoneServiceInstance;

// Add cleanup on window unload
window.addEventListener('beforeunload', () => {
  openPhoneServiceInstance.destroy();
}); 